import ButtonT from '@components/tailwind-components/Button/ButtonT';
import type { TExperimentResult } from '@conversed/shared/src/central/types/experiment-result';
import { useModal } from '@hooks/modal/useModal';
import type React from 'react';
import { useTranslation } from 'react-i18next';

interface ExperimentResultModalProps {
  experimentResult: TExperimentResult;
}

export const ExperimentResultModal: React.FC<ExperimentResultModalProps> = ({
  experimentResult,
}) => {
  const { t } = useTranslation();
  const { hideModal } = useModal();

  return (
    <div className='tailwind-modal-form flex-1 px-2'>
      <div className='tailwind-modal-body'>
        <div className='mb-6 p-4 bg-gray-100 rounded-lg'>
          <div className='grid grid-cols-2 gap-4 text-center mb-3'>
            <div>
              <span className='text-tiny sm:text-xs text-gray-500 block mb-1'>
                {t('experiment-result.modal.experiment-name')}
              </span>
              <span className='text-xs sm:text-sm font-medium text-gray-800'>
                {experimentResult.experimentName}
              </span>
            </div>
            <div>
              <span className='text-tiny sm:text-xs text-gray-500 block mb-1'>
                {t('experiment-result.modal.dataset')}
              </span>
              <span className='text-xs sm:text-sm font-medium text-gray-800'>
                {experimentResult.datasetName}
              </span>
            </div>
          </div>
          <div className='grid grid-cols-3 gap-4 text-center mb-3'>
            <div>
              <span className='text-tiny sm:text-xs text-gray-500 block mb-1'>
                {t('test-accuracy.table.header.start-date')}
              </span>
              <span className='text-xs sm:text-sm font-medium text-gray-800'>
                {new Date(experimentResult.startDate).toLocaleString()}
              </span>
            </div>
            <div>
              <span className='text-tiny sm:text-xs text-gray-500 block mb-1'>
                {t('test-accuracy.table.header.end-date')}
              </span>
              <span className='text-xs sm:text-sm font-medium text-gray-800'>
                {new Date(experimentResult.endDate).toLocaleString()}
              </span>
            </div>
            <div>
              <span className='text-tiny sm:text-xs text-gray-500 block mb-1'>
                {t('experiment-result.table.header.score')}
              </span>
              <span className='text-xs sm:text-sm font-medium'>
                {experimentResult.score}%
              </span>
            </div>
          </div>
          {(experimentResult.totalTokens != null ||
            experimentResult.avgLatencyMs != null) && (
            <div className='grid grid-cols-3 gap-4 text-center pt-3 border-t border-gray-200'>
              <div>
                <span className='text-tiny sm:text-xs text-gray-500 block mb-1'>
                  {t('experiment-result.modal.total-tokens')}
                </span>
                <span className='text-xs sm:text-sm font-medium text-gray-800'>
                  {experimentResult.totalTokens != null
                    ? experimentResult.totalTokens.toLocaleString()
                    : '-'}
                </span>
              </div>
              <div>
                <span className='text-tiny sm:text-xs text-gray-500 block mb-1'>
                  {t('experiment-result.modal.prompt-tokens')}
                </span>
                <span className='text-xs sm:text-sm font-medium text-gray-800'>
                  {experimentResult.totalPromptTokens != null
                    ? experimentResult.totalPromptTokens.toLocaleString()
                    : '-'}
                </span>
              </div>
              <div>
                <span className='text-tiny sm:text-xs text-gray-500 block mb-1'>
                  {t('experiment-result.modal.avg-latency')}
                </span>
                <span className='text-xs sm:text-sm font-medium text-gray-800'>
                  {experimentResult.avgLatencyMs != null
                    ? `${(experimentResult.avgLatencyMs / 1000).toFixed(1)}s`
                    : '-'}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className='w-full max-h-[500px] overflow-y-auto border-2 border-gray-200 rounded-tl-md rounded-tr-md'>
          <table className='w-full table-fixed'>
            <thead className='align-middle text-left font-medium bg-white sticky top-0 z-10'>
              <tr className='bg-gray-100 text-center text-tiny xs:text-xs sm:text-sm'>
                <th className='bg-inherit rounded-tl-md px-2 py-2 w-1/4'>
                  <span className='text-primary-base font-medium'>
                    {t('test-results.modal.table.header.question')}
                  </span>
                </th>
                <th className='bg-inherit px-2 py-2 w-1/4'>
                  <span className='text-primary-base font-medium'>
                    {t('experiment-result.modal.table.header.reference-answer')}
                  </span>
                </th>
                <th className='bg-inherit px-2 py-2 w-1/4'>
                  <span className='text-primary-base font-medium'>
                    {t('experiment-result.modal.table.header.bot-answer')}
                  </span>
                </th>
                <th className='bg-inherit px-2 py-2 rounded-tr-md w-1/4'>
                  <span className='text-primary-base font-medium'>
                    {t('experiment-result.modal.table.header.reasoning')}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody className='font-normal'>
              {experimentResult.questionResults.map((row, index) => (
                <tr
                  key={row.questionId || index}
                  className={`border-b border-gray-200 text-gray-800 text-center whitespace-nowrap text-tiny xs:text-xs sm:text-sm ${
                    row.isCorrect ? 'bg-green-light/10' : 'bg-red-light/40'
                  }`}>
                  <td className='px-2 py-2 align-top text-left bg-inherit border-gray-200'>
                    <div className='whitespace-normal break-words'>
                      <span className='text-xs'>{row.question}</span>
                    </div>
                  </td>
                  <td className='px-2 py-2 align-top text-left bg-inherit border-gray-200'>
                    <div className='whitespace-normal break-words'>
                      <span className='text-xs'>{row.referenceAnswer}</span>
                    </div>
                  </td>
                  <td className='px-2 py-2 align-top text-left bg-inherit border-gray-200'>
                    <div className='whitespace-normal break-words'>
                      <span className='text-xs'>{row.newAnswer}</span>
                    </div>
                  </td>
                  <td className='px-2 py-2 align-top text-left bg-inherit border-gray-200'>
                    <div className='whitespace-normal break-words'>
                      <span className='text-xs italic text-gray-600'>
                        {row.reasoning}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className='tailwind-modal-footer'>
        <ButtonT
          onClick={hideModal}
          type='button'
          styling='cancel'
          size='xs'
          rounding='md'>
          {t('general.buttons.close')}
        </ButtonT>
      </div>
    </div>
  );
};
